/**
 * AdminTools
 *
 * Landing page for tools like Adjust Tags and Tag Counts. Redirects non-admins.
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/auth';
import Footer from '../../../components/shared/Footer';
import { LoadingSpinner } from '../../../components/ui/feedback/loading-spinner.jsx';
import { Wrench, Tags, BarChart3, ArrowLeft, PhoneCall, AlertTriangle } from 'lucide-react';
import api from '../../../api';
import { isUserAdmin } from '../../../utils/admin/adminUtils';

export default function AdminTools() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  
  // Check if user is the admin using the same pattern as other admin pages
  const isAdmin = isUserAdmin(user);

  // Check if user is an admin, if not, redirect to home page
  useEffect(() => {
    if (user) {
      if (!isAdmin) {
        navigate('/app');
      }
      setIsLoading(false);
    } else if (user === null) {
      // No user, redirect to login
      navigate('/login');
    }
  }, [navigate, user, isAdmin]);

  // Tools admin pages
  const toolPages = [
    {
      title: 'Adjust Tags',
      description: 'Manage and organize tags for content categorization',
      icon: Tags,
      path: '/app/private/adjustTags',
      color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
    },
    {
      title: 'Tag Counts',
      description: 'View usage statistics and analytics for all tags',
      icon: BarChart3,
      path: '/app/private/tagCounts',
      color: 'bg-orange-50 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300',
    }
  ];

  const [resetLoading, setResetLoading] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const handleResetContactFlags = async () => {
    setResetLoading(true);
    setResetResult(null);
    try {
      const res = await api.post('/admin/contact-info/reset-flag', {});
      setResetResult(res?.data || res);
    } catch (err) {
      setResetResult({ error: err?.response?.data?.error || err.message });
    } finally {
      setResetLoading(false);
      setConfirmOpen(false);
    }
  };

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center h-screen">
        <LoadingSpinner message="Loading tools admin..." />
      </div>
    );
  }

  if (!isAdmin) {
    return null; // Redirect happens in useEffect
  }

  return (
    <div className="p-6 max-w-[1600px] mx-auto pb-20">
      <div className="mb-8">
        <div className="flex items-center mb-4">
          <button
            onClick={() => navigate('/app/private/admin')}
            className="flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mr-4 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded-lg p-1"
          >
            <ArrowLeft className="h-5 w-5 mr-1" />
            <span className="text-sm font-medium">Back to Admin</span>
          </button>
        </div>
        <div className="flex items-center mb-4">
          <Wrench className="h-6 w-6 text-primary-600 dark:text-primary-400 mr-2" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">Administrative Tools</h1>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Manage tags, view analytics, and access other administrative tools.
        </p>
      </div>

      {/* Contact Info Campaign tool */}
      <div className="mb-8 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-5 shadow-sm">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <PhoneCall className="h-5 w-5 text-primary-600 dark:text-primary-400 mr-2" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-white">Contact Info Campaign</h2>
          </div>
          <button
            onClick={() => setConfirmOpen(true)}
            disabled={resetLoading}
            className={`px-3.5 py-2 rounded-md text-white text-sm font-medium ${resetLoading ? 'bg-gray-500' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'}`}
          >
            {resetLoading ? 'Running…' : 'Reset flags'}
          </button>
        </div>
        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
          Sets <b>hasSeenContactInfoModal</b> to <b>false</b> only for users missing a phone number.
        </p>

        {/* Minimal confirm modal */}
        {confirmOpen && (
          <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/40">
            <div className="w-full max-w-sm rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 p-5 shadow-xl">
              <div className="flex items-center mb-3">
                <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2" />
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Confirm reset</h3>
              </div>
              <p className="text-sm text-gray-700 dark:text-gray-300">
                This will mark the contact-info modal as not seen for users <b>without a phone number</b> so they are prompted again. Proceed?
              </p>
              <div className="mt-4 flex gap-2 justify-end">
                <button
                  onClick={() => setConfirmOpen(false)}
                  className="px-3 py-1.5 rounded-md text-sm border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  Cancel
                </button>
                <button
                  onClick={handleResetContactFlags}
                  disabled={resetLoading}
                  className={`px-3.5 py-1.5 rounded-md text-white text-sm font-medium ${resetLoading ? 'bg-gray-500' : 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700'}`}
                >
                  {resetLoading ? 'Running…' : 'Confirm reset'}
                </button>
              </div>
              {resetResult && (
                <div className={`mt-3 text-xs ${resetResult.error ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                  {resetResult.error ? resetResult.error : `Updated ${resetResult.updated || 0} users`}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tools pages grid */}
      <div className="flex flex-col gap-4">
        {toolPages.map((page, index) => (
          <button
            key={page.path || `tool-page-${index}`}
            onClick={page.path ? () => navigate(page.path) : () => {}}
            className="flex items-center w-full p-6 bg-white dark:bg-transparent border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 text-left focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900"
          >
            <div className={`p-2 rounded-lg ${page.color} mr-4`}>
              <page.icon className="h-5 w-5" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                {page.title}
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {page.description}
              </p>
            </div>
            <div className="w-6 flex items-center justify-center h-5">
              <svg className="h-4 w-4 text-primary-600 dark:text-primary-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </div>
          </button>
        ))}
      </div>

      <Footer />
    </div>
  );
} 